import { Button, Grid } from "@material-ui/core";
import ModalAddress from "@saleor/changeOrderDomestic/components/ModalAddress";
import ModalCreditMaster from "@saleor/changeOrderDomestic/components/ModalCreditMaster";
import DatePicker from "@saleor/components/DatePicker";
import MyTypography from "@saleor/components/MyTypography";
import { SingleTextField } from "@saleor/components/SingleTextField";
import Skeleton from "@saleor/components/Skeleton";
import {
  ContractOrderDomesticQuery,
  ContractQuery,
  Get_Realtime_PartnerMutation,
  RealtimePartnerType,
  useGetCreditLimitLazyQuery
} from "@saleor/graphql";
import CreditCardIcon from "@saleor/icons/CreditCard";
import { makeStyles } from "@saleor/macaw-ui";
import classNames from "classnames";
import { FormikProps } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

interface PartnerTabProps {
  formik: FormikProps<any>;
  realtimePartnerData: Get_Realtime_PartnerMutation;
  getRealtimePartnerData: () => void;
  data: ContractOrderDomesticQuery["contractOrder"];
  contract: {
    billTo: string;
    shipTo: string;
  };
  dataContract: ContractQuery;
  setBillToInsteaded: (value: string) => void;
  setShipToInsteaded: (value: string) => void;
  onApplyAll: () => void;
}

const useStyles = makeStyles(
  theme => ({
    rowFlex: {
      display: "flex",
      alignItems: "center"
    },
    flexRight: {
      marginLeft: "auto"
    },
    btnThin: {
      height: 32
    },
    boxBackground: {
      borderRadius: 4,
      background: theme.palette.background.default
    },
    boxSoldTo: {
      padding: 12,
      margin: "10px 0 24px 0"
    },
    colorInfo: {
      color: theme.palette.info.dark
    },
    boxCustomer: {
      padding: "16px 12px",
      margin: "10px 0 20px 0",
      "& .MuiTypography-root": {
        margin: "10px 0"
      },
      "& .MuiFormControl-root": {
        height: "4rem"
      }
    },
    boxGrid: {
      "& .MuiTypography-root": {
        margin: "10px 0"
      },
      "& .MuiFormControl-root": {
        height: "4rem"
      }
    },
    textError: {
      color: theme.palette.error.light,
      marginTop: 5
    },
    normalText: {
      fontWeight: "normal"
    },
    rowTitle: {
      marginBottom: 10
    },
    rowContent: {
      marginBottom: 30
    },
    required: {
      color: theme.palette.error.main,
      marginLeft: 1,
      "& :after": {
        content: "*"
      }
    },
    applyBtn: {
      width: "100%",
      height: "42px",
      padding: "0.6rem",
      fontSize: "14px",
      color: "white",
      backgroundColor: "#222",
      "& .MuiButton-label": {
        fontWeight: "700"
      }
    },
    paymentTermAlignment: {
      width: "100%",
      height: "50%",
      display: "flex",
      alignItems: "center",
      overflow: "auto",
      margin: "auto !important",
      padding: "1rem 0"
    }
  }),
  { name: "PartnerTab" }
);

const PartnerTab: React.FC<PartnerTabProps> = props => {
  const {
    formik,
    data,
    setShipToInsteaded,
    setBillToInsteaded,
    dataContract,
    realtimePartnerData,
    getRealtimePartnerData,
    onApplyAll
  } = props;
  const { customer } = data || {};
  const classes = useStyles();
  const intl = useIntl();

  const [openModalAddress, setOpenModalAddress] = useState<boolean>(false);
  const [typeAddress, setTypeAddress] = useState<RealtimePartnerType>(
    RealtimePartnerType.SHIP_TO
  );
  const [openModalCreditMaster, setOpenModalCreditMaster] = useState<boolean>(
    false
  );

  const [
    callCreditLimit,
    { data: dataCreditLimit, loading: loadingCreditLimit }
  ] = useGetCreditLimitLazyQuery({
    variables: {
      input: {
        contractNo: dataContract?.contract?.contractNo,
        salesOrgCode: dataContract?.contract?.salesOrganization?.code,
        soldToCode: customer?.soldToCode
      }
    },
    fetchPolicy: "no-cache"
  });

  const handleCloseModalAddress = () => {
    setOpenModalAddress(false);
  };

  useEffect(() => {
    if (dataCreditLimit?.getCreditLimit) {
      setOpenModalCreditMaster(true);
    }
  }, [dataCreditLimit]);

  const handleOpenModalAddress = (type: RealtimePartnerType) => {
    setTypeAddress(type);
    getRealtimePartnerData();
    setOpenModalAddress(true);
  };

  const handleApplyAddress = (address, type) => {
    if (type === RealtimePartnerType.SHIP_TO) {
      setShipToInsteaded(address);
      formik.setFieldValue("shipTo", address);
    }
    if (type === RealtimePartnerType.BILL_TO) {
      formik.setFieldValue("billTo", address);
      setBillToInsteaded(address);
    }
  };

  const handleOpenCreditMaster = () => {
    callCreditLimit();
  };

  const mappedRealtimePartnerData = useMemo(
    () => realtimePartnerData?.getSapRealtimePartner?.response,
    [realtimePartnerData]
  );

  const getRealTimePartnerDataByType = () => {
    if (typeAddress === RealtimePartnerType.SHIP_TO) {
      return mappedRealtimePartnerData?.filter(e => e.partnerFunction === "WE");
    }
    if (typeAddress === RealtimePartnerType.BILL_TO) {
      return mappedRealtimePartnerData?.filter(e => e.partnerFunction === "RE");
    }
  };

  const options = useMemo(() => getRealTimePartnerDataByType(), [
    typeAddress,
    realtimePartnerData
  ]);

  const getTextError = field =>
    formik.errors[field] && formik.touched[field] ? (
      <div className={classes.textError}>{formik.errors[field]}</div>
    ) : (
      ""
    );

  const handleDateChange = e => {
    formik.handleChange(e);
    formik.setFieldTouched(e?.target?.name, true, true);
  };

  const handlePODate = e => {
    formik.handleChange(e);
  };

  const soldToText = customer
    ? `${customer.soldToCode} - ${customer.soldToName}`
    : "";

  return (
    <>
      <Grid container spacing={6}>
        <Grid item xs={12} md={6}>
          <div className={classes.rowFlex}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({ defaultMessage: " Sold-to Party" })}
            </MyTypography>
            <Button
              id="openCreditMasterButton"
              data-testid="openCreditMasterButton"
              variant={"outlined"}
              color={"secondary"}
              className={classNames(classes.flexRight, classes.btnThin)}
              onClick={handleOpenCreditMaster}
              disabled={loadingCreditLimit}
              startIcon={
                <CreditCardIcon style={{ marginRight: 0, marginLeft: 4 }} />
              }
            >
              <MyTypography variant={"body3"}>
                {intl.formatMessage({ defaultMessage: "Credit Master" })}
              </MyTypography>
            </Button>
          </div>
          <div className={classNames(classes.boxBackground, classes.boxSoldTo)}>
            <MyTypography variant={"body1"} className={classes.colorInfo}>
              {customer ? soldToText : <Skeleton />}
            </MyTypography>
            <MyTypography variant={"body3"}>{data?.soldToAddress}</MyTypography>
          </div>
          <Grid container spacing={3} className={classes.boxGrid}>
            <Grid item xs={12} md={6} lg={5}>
              <MyTypography variant={"subtitle3"}>
                PO No.<span className={classes.required}>*</span>
              </MyTypography>
              <SingleTextField
                onBlur={formik.handleBlur}
                value={formik.values.poNo}
                onChange={formik.handleChange}
                name={"poNo"}
                id="poNo"
                error={!!formik.errors.poNo && !!formik.touched.poNo}
                maxLengthProps={10}
                placeholder="ระบุเลขที่ใบสั่งซื้อ"
              />
              {getTextError("poNo")}
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              <MyTypography variant={"subtitle3"}>Payment Term</MyTypography>
              <MyTypography
                variant={"body3"}
                className={classes.paymentTermAlignment}
              >
                {`${dataContract?.contract?.paymentTermKey} - ${dataContract?.contract?.paymentTermDescTh}`}
              </MyTypography>
            </Grid>
            <Grid item xs={12} lg={5}>
              <MyTypography variant={"subtitle3"}>PO Date</MyTypography>
              <DatePicker
                noBlock
                value={formik.values.poDate}
                onChange={handlePODate}
                onAccept={formik.handleBlur}
                name={"poDate"}
                id="poDate"
              />
            </Grid>
            <Grid item xs={12} md={12} lg={7}>
              <Grid container alignItems="flex-end" spacing={2}>
                <Grid item xs={7}>
                  <MyTypography variant={"subtitle3"}>
                    Request Date<span className={classes.required}>*</span>
                  </MyTypography>
                  <DatePicker
                    noBlock
                    value={formik.values.requestDate}
                    onChange={handleDateChange}
                    onAccept={handleDateChange}
                    name={"requestDate"}
                    error={
                      !!formik.errors.requestDate &&
                      !!formik.touched.requestDate
                    }
                    id="requestDate"
                  />
                </Grid>
                <Grid item xs={5}>
                  <Button
                    variant="contained"
                    className={classes.applyBtn}
                    onClick={onApplyAll}
                    disabled={false}
                    name="applyAllButton"
                    id="applyAllButton"
                    data-testid="apply-all-partner-button"
                  >
                    {intl.formatMessage({
                      defaultMessage: "Apply all",
                      description: "button label"
                    })}
                  </Button>
                </Grid>
              </Grid>
              {getTextError("requestDate")}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <div className={classes.rowFlex}>
            <MyTypography variant={"subtitle3"} className={classes.rowTitle}>
              Ship-to Party
            </MyTypography>

            {(mappedRealtimePartnerData?.filter(e => e.partnerFunction === "WE")
              ?.length || 0) > 1 ? (
              <Button
                id="changeShipToPartyButton"
                data-testid="changeShipToPartyButton"
                variant={"outlined"}
                color={"secondary"}
                className={classNames(classes.flexRight, classes.btnThin)}
                onClick={() =>
                  handleOpenModalAddress(RealtimePartnerType.SHIP_TO)
                }
              >
                <MyTypography variant={"body3"}>
                  {intl.formatMessage({ defaultMessage: "แก้ไข" })}
                </MyTypography>
              </Button>
            ) : null}
          </div>
          <MyTypography
            style={{
              whiteSpace: "pre-line"
            }}
            variant={"body3"}
            className={classes.rowContent}
            data-testid="shipToParty-text"
          >
            {formik.values.shipTo}
          </MyTypography>
          <div className={classes.rowFlex}>
            <MyTypography variant={"subtitle3"} className={classes.rowTitle}>
              Bill-to Party
            </MyTypography>

            {(mappedRealtimePartnerData?.filter(e => e.partnerFunction === "RE")
              ?.length || 0) > 1 ? (
              <Button
                id="changeBillToPartyButton"
                data-testid="changeBillToPartyButton"
                variant={"outlined"}
                color={"secondary"}
                className={classNames(classes.flexRight, classes.btnThin)}
                onClick={() =>
                  handleOpenModalAddress(RealtimePartnerType.BILL_TO)
                }
              >
                <MyTypography variant={"body3"}>
                  {intl.formatMessage({ defaultMessage: "แก้ไข" })}
                </MyTypography>
              </Button>
            ) : null}
          </div>
          <MyTypography
            style={{
              whiteSpace: "pre-line"
            }}
            variant={"body3"}
            className={classes.rowContent}
            data-testid="billToParty-text"
          >
            {formik.values.billTo}
          </MyTypography>
        </Grid>
      </Grid>
      {openModalAddress && (
        <ModalAddress
          options={options}
          onClose={handleCloseModalAddress}
          visible={openModalAddress}
          type={typeAddress}
          handleApplyAddress={handleApplyAddress}
          formik={formik}
          partnerName=""
          partnerNo=""
        />
      )}
      <ModalCreditMaster
        title="Credit Master"
        dataModal={dataCreditLimit?.getCreditLimit}
        open={openModalCreditMaster}
        onClose={() => setOpenModalCreditMaster(false)}
        soldTo={soldToText}
      />
    </>
  );
};

export default PartnerTab;
